import { inject, Injectable, Signal } from '@angular/core';
import { StoreService } from '@webapp/core/services/store.service';
import { EntityDetailStore } from '@webapp/core/stores/entity-detail-store.service';
import { EntityListStore } from '@webapp/core/stores/entity-list-store.service';
import { PaginationParams } from '@webapp/core/ui/components/pagination/pagination.interfaces';
import { firstValueFrom, map } from 'rxjs';
import { getBadgeAndActions } from '../common/map';
import {
	IQuoteDetail,
	IQuoteRow,
	IQuoteWithInsurable,
} from '../interfaces/quotes.interface';
import { QuotesService } from '../services/quotes.service';
import { ICoverage } from '@webapp/payments';

export interface QuotesState {
	showSidebar: boolean;
	selectedCoverage: ICoverage | undefined;
	selectedOptionalCoverages: ICoverage[];
	loadingDownload: boolean;
}

@Injectable({
	providedIn: 'root',
})
export class QuotesStoreService extends StoreService<QuotesState> {
	listStore: EntityListStore<IQuoteRow>;
	detailStore: EntityDetailStore<IQuoteDetail>;
	service = inject(QuotesService);

	constructor() {
		super();
		this.initializeState();

		this.listStore = new EntityListStore<IQuoteRow>(
			(params: PaginationParams) =>
				firstValueFrom(
					this.service.getQuotes(params).pipe(
						map(row => ({
							...row,
							data: row.data.map(quote => ({
								...quote,
								...getBadgeAndActions(quote),
							})),
						}))
					)
				)
		);
		this.detailStore = new EntityDetailStore<IQuoteDetail>((id: string) =>
			firstValueFrom(
				this.service.getQuote(id).pipe(
					map(row => {
						this.set(
							'selectedCoverage',
							row.coverages.find(c => c.select)
						);
						return {
							...row,
							...getBadgeAndActions(row),
						};
					})
				)
			)
		);
	}
	private initializeState() {
		this.setState({
			loadingDownload: false,
			showSidebar: false,
			selectedCoverage: undefined,
			selectedOptionalCoverages: [],
		});
	}

	public get(): Signal<IQuoteWithInsurable | null> {
		return this.detailStore.getSelected();
	}

	public getRows(): Signal<IQuoteRow[]> {
		return this.listStore.select('rows');
	}

	public cleanSelectedQuote() {
		this.set('showSidebar', false);
		this.detailStore.set('selected', null);
	}

	toggleOptionalCoverage(coverage: ICoverage) {
		const selectedOptionalCoverages = this.select(
			'selectedOptionalCoverages'
		)();
		const index = selectedOptionalCoverages.findIndex(
			c => c.id === coverage.id
		);

		if (index === -1) {
			selectedOptionalCoverages.push(coverage);
		} else {
			selectedOptionalCoverages.splice(index, 1);
		}

		this.set('selectedOptionalCoverages', selectedOptionalCoverages);
	}

	async downloadQuote() {
		const quote = this.get()();

		if (quote) {
			this.set('loadingDownload', true);
			const blob = await firstValueFrom(
				this.service.downloadQuote(quote.id)
			);
			this.set('loadingDownload', false);
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `cotizacion_${quote.id}.pdf`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			window.URL.revokeObjectURL(url);
		}
	}
}
